import { isEVMChain, isSolanaChain, type Evaluate, type SolanaProvider } from '@particle-network/connector-core';
import { createPlugin, type Plugin } from '@particle-network/plugin-core';
import {
  walletEntryPlugin,
  type CustomMessageType,
  type EthereumProvider,
  type SolanaWallet,
  type WalletCore,
  type WalletEntryPlugin,
  type WalletOption,
} from '@particle-network/wallet';
import { createSolanaWallet } from './createSolanaWallet';

export type WalletPlugin = Plugin<WalletEntryPlugin, void, WalletPluginProperties>;

export type OpenWalletParamers = Evaluate<Parameters<WalletEntryPlugin['openWallet']>[0]>;
export type GetWalletIFrameParamers = Evaluate<Parameters<WalletEntryPlugin['getWalletIFrame']>[0]>;
export type InitParamers = Evaluate<Omit<WalletOption, 'widgetIntegration' | 'entryPosition' | 'visible' | 'preload'>>;

export type WalletPluginProperties = {
  getWalletIFrame: (parameters?: GetWalletIFrameParamers) => ReturnType<WalletEntryPlugin['getWalletIFrame']>;
  openWallet: (parameters?: OpenWalletParamers) => void;
  destory: () => void;
  init: (parameters?: InitParamers) => void;
  overrideWalletOption: (parameters: Evaluate<Parameters<WalletEntryPlugin['overrideWalletOption']>[0]>) => void;
};

export type WalletPluginParamers = Evaluate<
  Pick<WalletOption, 'widgetIntegration' | 'entryPosition' | 'visible' | 'preload' | 'themeType' | 'walletUrl'> & {
    customStyle?: Omit<WalletOption['customStyle'], 'supportChains'>;
  }
>;

enum MessageType {
  OpenAccountAndSecurity = 'open-account-and-security',
  GetUserInfo = 'get-user-info',
  GetPasskeyOption = 'get-passkey-option',
}

export const wallet = (parameters: WalletPluginParamers = {}) => {
  const { widgetIntegration, entryPosition, visible, preload, themeType, customStyle, walletUrl } = parameters;

  return createPlugin<WalletEntryPlugin, void, WalletPluginProperties>((config) => ({
    get id() {
      return 'wallet';
    },
    get name() {
      return 'Particle Wallet';
    },
    getPlugin() {
      return walletEntryPlugin;
    },
    async onConnect(connection) {
      const connector = connection[0].connector;
      const provider: any = await connector.getProvider();
      const walletCore: WalletCore = {};
      if (connector.type === 'particleAuth') {
        if (connector.chainType === 'evm') {
          walletCore.ethereum = provider as EthereumProvider;
        } else {
          walletCore.solana = provider as SolanaWallet;
        }
      } else {
        if (connector.chainType === 'evm') {
          walletCore.ethereum = provider as unknown as EthereumProvider;
        } else {
          if ((this.getPlugin().walletCore?.solana as any)?.provider == provider) {
            walletCore.solana = this.getPlugin().walletCore?.solana;
          } else {
            const chainId = await connector.getChainId();
            walletCore.solana = createSolanaWallet(provider as SolanaProvider, chainId);
          }
        }
      }
      if (
        this.getPlugin().walletCore?.ethereum != walletCore.ethereum ||
        this.getPlugin().walletCore?.solana != walletCore.solana
      ) {
        // set custom event handler
        if (connector.type === 'particleAuth') {
          const customEventHandler = async (messageType: CustomMessageType) => {
            if (messageType === MessageType.OpenAccountAndSecurity) {
              (window as any).particle?._internal?.openAccountAndSecurity();
            } else if (messageType === MessageType.GetUserInfo) {
              return await (window as any).particle?._internal.getUserInfo();
            }
          };
          customEventHandler.type = 'auth' as const;
          walletCore.customEventHandler = customEventHandler;
        } else if (provider.isPasskey && provider.getPasskeyOption) {
          // passkey + aa
          const customEventHandler = async (messageType: string) => {
            if (messageType === MessageType.GetPasskeyOption) {
              const option = await await provider.getPasskeyOption();
              return option;
            }
          };
          customEventHandler.type = 'passkey' as const;
          walletCore.customEventHandler = customEventHandler;
        }

        const validChains = config.chains.filter((chain) =>
          connector.chainType === 'evm' ? isEVMChain(chain) : isSolanaChain(chain)
        );
        this.getPlugin().overrideWalletOption({
          entryPosition,
          visible,
          preload,
          customStyle: {
            ...this.getPlugin().walletOptions?.customStyle,
            supportChains: validChains.map((chain) => ({
              id: chain.id,
              chainType: connector.chainType,
            })),
          },
        });

        console.log('wallet plugin setWalletCore', walletCore);
        this.getPlugin().setWalletCore(walletCore);
        this.getPlugin().walletEntryCreate();
      } else {
        if (!this.getPlugin().walletCreated) {
          const validChains = config.chains.filter((chain) =>
            connector.chainType === 'evm' ? isEVMChain(chain) : isSolanaChain(chain)
          );
          this.getPlugin().overrideWalletOption({
            customStyle: {
              ...this.getPlugin()?.walletOptions?.customStyle,
              supportChains: validChains.map((chain) => ({
                id: chain.id,
                chainType: connector.chainType,
              })),
            },
          });
          this.getPlugin().walletEntryCreate();
        }
      }
    },
    async onDisconnect() {
      this.getPlugin().walletEntryDestroy();
    },
    getWalletIFrame(parameters) {
      return this.getPlugin().getWalletIFrame(parameters);
    },
    openWallet(parameters) {
      return this.getPlugin().openWallet(parameters);
    },
    destory() {
      this.getPlugin().destroy();
    },
    init(parameters) {
      this.getPlugin().init(config.projectConfig, {
        // 可以被覆盖
        entryPosition,
        visible,
        preload,

        ...parameters,
        widgetIntegration,
        walletUrl,
        themeType: themeType ?? parameters?.themeType,
        customStyle: {
          ...parameters?.customStyle,
          ...customStyle,
        },
      });
    },
    overrideWalletOption(parameters) {
      const mergeOptions = {
        // 可以被覆盖
        entryPosition,
        visible,
        preload,

        ...parameters,
        widgetIntegration,
        themeType: themeType ?? parameters?.themeType,
        customStyle: {
          ...parameters?.customStyle,
          ...customStyle,
          supportChains:
            parameters?.customStyle?.supportChains || this.getPlugin().walletOptions.customStyle?.supportChains,
        },
      };
      console.log('wallet plugin overrideWalletOption', mergeOptions);
      this.getPlugin().overrideWalletOption(mergeOptions);
    },
  }));
};
